import { SkeletonLoader, SkeletonLoaderArea } from '@design/components/skeleton-loader/SkeletonLoader';
import { usePriceChangeMarketReactionData } from '@terminal/hooks/use-price-change-market-reaction-data/usePriceChangeMarketReactionData';
import { usePriceMarketReactionData } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { CatalystMarketReactionChart } from '@terminal/modules/market-reaction-charts/catalyst-market-reaction-chart/CatalystMarketReactionChart';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import React from 'react';

import styles from './MarketCatalystReaction.module.scss';

const skeletonLoaderAreas: SkeletonLoaderArea[] = [
  {
    styles: {
      width: '100%',
      height: '400px',
      borderRadius: '8px',
    },
  },
];

export interface MarketCatalystReactionProps {
  tags: string[];
  tickers: string[];
  createdAt: string;
  leftHeaderNode?: React.ReactNode;
}

export const MarketCatalystReaction = ({
  tags,
  tickers,
  createdAt,
  leftHeaderNode,
}: MarketCatalystReactionProps) => {
  const isMultipleETFs = tags.length > 1;
  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag: tags[0],
    ticker: tickers[0],
    reportingDate: createdAt,
    isMultipleEntitiesChart: isMultipleETFs,
    variant: 'catalyst',
    isMarketCatalyst: true,
  });

  const { data: entitiesData } = useEntities({
    entityTags: tags,
  });

  const {
    data: priceChangeData,
    isLoading: isPriceChangeMarketReactionDataLoading,
  } = usePriceChangeMarketReactionData({
    tags,
    tickers,
    fromDateEST,
    toDateEST,
    enabled: isMultipleETFs,
  });

  const isLoading =
    isPriceMarketReactionDataLoading || isPriceChangeMarketReactionDataLoading;

  const lines = isMultipleETFs
    ? priceChangeData
    : [
        {
          data: priceData,
          prevCloseData,
          lastProportionChange,
          lastKnownPrice,
          lastKnownDate,
        },
      ];

  if (isLoading) {
    return (
      <div className={styles.skeletonLoaderWrapper}>
        <SkeletonLoader areas={skeletonLoaderAreas} />
      </div>
    );
  }

  return (
    <CatalystMarketReactionChart
      fromDateEST={fromDateEST}
      toDateEST={toDateEST}
      lines={lines}
      entities={entitiesData}
      leftHeaderNode={leftHeaderNode}
    />
  );
};
